import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  effect,
  inject,
  OnInit,
  signal,
  untracked,
  ViewEncapsulation
} from '@angular/core';
import {
  CardContainerComponent,
  closePopupData,
  ControlRegexSignalDirective,
  createErrorPopupData,
  createSuccessPopupData,
  CustomLineSkeletonComponent,
  IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX,
  InformationCardComponent,
  LiveControlRegexSignalDirective,
  LoadingButtonComponent,
  NON_IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX,
  StatusMessagePopupV2Component,
  TextSignalInputComponent,
  ToUppercaseSignalDirective,
  TRANSLATE_SERVICE_TOKEN,
  TranslateInterface,
  VIN_NUMBER_REGEX,
  VIN_NUMBER_REGEX_INPUT
} from '@tribeauto/ngx-mobilite-material';
import { MatCheckbox } from '@angular/material/checkbox';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { PaperDeliveryData } from './paper-delivery.model';
import { ApplicationStore } from '../../../../../core/services/application-store.service';
import { ActivatedRoute, Router } from '@angular/router';
import { vehicleCategoriesEligibleToRegistration } from '../demat-delivery/demat-delivery.component';
import { EsivService } from '../esiv.service';
import { catchError, filter, finalize, of, tap } from 'rxjs';
import { mapToSivSearch } from '../esiv.mapper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FinancialContractService } from '../financial-contract.service';
import { FinancingRequest } from '../demat-delivery/demat-delivery.component.model';
import { AUTO_CLOSE_DELAY_TEN_SECONDS } from '../../../../helpers/popup.helper';
import {
  debounce,
  disabled,
  form,
  FormField,
  hidden,
  maxLength,
  minLength,
  required,
  validate
} from '@angular/forms/signals';

@Component({
  selector: 'pvmo-paper-delivery',
  imports: [
    CardContainerComponent,
    InformationCardComponent,
    MatCheckbox,
    LoadingButtonComponent,
    ReactiveFormsModule,
    CustomLineSkeletonComponent,
    FormsModule,
    TranslatePipe,
    StatusMessagePopupV2Component,
    FormField,
    TextSignalInputComponent,
    ToUppercaseSignalDirective,
    LiveControlRegexSignalDirective,
    ControlRegexSignalDirective
  ],
  providers: [
    {
      provide: TRANSLATE_SERVICE_TOKEN,
      useFactory: (translateService: TranslateService): TranslateInterface => ({
        get: (key: string): string => translateService.instant(key)
      }),
      deps: [TranslateService]
    }
  ],
  templateUrl: './paper-delivery.component.html',
  styleUrl: './paper-delivery.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class PaperDeliveryComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly applicationStore = inject(ApplicationStore);
  private readonly esivService = inject(EsivService);
  private readonly financialContractService = inject(FinancialContractService);
  private readonly translateService = inject(TranslateService);
  private readonly cdr = inject(ChangeDetectorRef);

  private readonly vehicleNotFoundForImmat = signal('');

  vehicleData = this.applicationStore.instant('vehicleData');
  isVehicleUsed = signal(!!this.vehicleData()?.used);
  webstoreData = this.applicationStore.instant('webstoreData');
  externalId = this.activatedRoute.parent?.snapshot.paramMap.get('id');
  immatRegex = this.vehicleData()?.import
    ? IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX
    : NON_IMPORTED_REGISTRATION_NUMBER_INPUT_REGEX;
  vinRegex = VIN_NUMBER_REGEX_INPUT;
  numImmatError = signal(false);
  paperDelivery = signal<PaperDeliveryData>({
    numImmat: this.vehicleData()?.registrationNumber ?? '',
    numVin: this.vehicleData()?.vinNumber ?? ''
  });
  paperDeliveryForm = form(this.paperDelivery, path => {
    hidden(path.numImmat, { when: () => !this.isVehicleCategoryEligibleToImmat() });
    required(path.numImmat, { when: () => this.isVehicleUsed() });
    debounce(path.numImmat, 500);
    validate(path.numImmat, ({ value }) =>
      this.numImmatError() && value().toUpperCase() === this.vehicleNotFoundForImmat().toUpperCase()
        ? { kind: 'vehicle-not-found' }
        : undefined
    );
    required(path.numVin);
    maxLength(path.numVin, 17);
    minLength(path.numVin, 17);
    validate(path.numVin, ({ value }) =>
      !value() || VIN_NUMBER_REGEX.test(value()) ? undefined : { kind: 'pattern' }
    );
    disabled(path.numVin, {
      when: ({ valueOf }) => !!valueOf(path.numImmat) && this.paperDeliveryForm.numImmat().valid()
    });
  });
  statusSuccessPopupElement = closePopupData(createSuccessPopupData('', '', '', undefined));
  statusErrorPopupElement = closePopupData(createErrorPopupData('', '', '', undefined));
  isDisabledButton = false;
  isDopcChecked = false;
  isGdprChecked = false;
  isOptInChecked = false;
  isFetchVehicleLoading = signal(false);
  loadingFinancingRequest = signal(false);

  get disableButtonFinancing(): boolean {
    return this.paperDeliveryForm().invalid() || this.isDisabledButton;
  }

  constructor() {
    effect(() => {
      const numImmat = this.paperDeliveryForm.numImmat().value();
      this.paperDeliveryForm.numImmat().errors();
      untracked(() => {
        if (this.paperDelivery().numImmat && this.paperDeliveryForm.numImmat().valid()) {
          this.fetchVehicle(numImmat.toUpperCase());
        }
        if (!numImmat) {
          this.paperDeliveryForm.numVin().value.set('');
        }
      });
    });
  }
  ngOnInit(): void {
    const registrationNumberFromContract = this.vehicleData()?.registrationNumber;
    if (registrationNumberFromContract) {
      this.fetchVehicle(registrationNumberFromContract.toUpperCase());
    }
  }

  isVehicleCategoryEligibleToImmat(): boolean {
    const category = this.vehicleData()?.category;
    return !!category && vehicleCategoriesEligibleToRegistration.includes(category);
  }

  goToDocument(): void {
    this.closeSuccessPopup();
    const token = this.activatedRoute.snapshot.queryParamMap.get('token');
    void this.router.navigate([`/contract-action-hub/${this.externalId}/documents`], {
      queryParams: { token }
    });
  }

  fetchVehicle(registrationNumber: string): void {
    this.isFetchVehicleLoading.set(true);
    this.esivService
      .fetchVehicle(mapToSivSearch(registrationNumber))
      .pipe(
        filter(response => !!response),
        tap(response => {
          const registrationNumberFromResponse = response[registrationNumber];
          if (registrationNumberFromResponse && registrationNumberFromResponse.immatSiv === registrationNumber) {
            this.paperDeliveryForm.numImmat()?.value.set(response[registrationNumber].immatSiv);
            this.paperDeliveryForm.numVin()?.value.set(response[registrationNumberFromResponse.immatSiv].vinNumber);
            this.numImmatError.set(false);
          } else {
            this.setRegistrationNumberAndSerialNumberValuesWhenError();
          }
          this.isFetchVehicleLoading.set(false);
        }),
        catchError(() => {
          this.setRegistrationNumberAndSerialNumberValuesWhenError();
          this.isFetchVehicleLoading.set(false);
          return of(null);
        })
      )
      .subscribe();
  }

  financeRequest(): void {
    this.loadingFinancingRequest.set(true);
    const webstoreId = this.webstoreData()?.webstoreId;
    const financingRequest: FinancingRequest = {
      holder: {
        dopc: this.isDopcChecked,
        gdpr: this.isGdprChecked,
        optIn: this.isOptInChecked
      },
      vehicle: {
        importedVehicle: this.vehicleData()?.import ?? false,
        registrationNumber: this.paperDeliveryForm.numImmat()?.value() ?? '',
        serialNumber: this.paperDeliveryForm.numVin()?.value() ?? ''
      }
    };
    if (webstoreId && this.externalId) {
      this.financialContractService
        .financeRequest(webstoreId, this.externalId, financingRequest)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          finalize(() => this.loadingFinancingRequest.set(false))
        )
        .subscribe({
          next: () => {
            this.isDisabledButton = true;
            this.setSuccessStatusPopup();
          },
          error: () => {
            this.setErrorStatusPopup();
          }
        });
    } else {
      this.setErrorStatusPopup();
      this.loadingFinancingRequest.set(false);
    }
  }

  closeSuccessPopup(): void {
    this.statusSuccessPopupElement = closePopupData(this.statusSuccessPopupElement);
  }

  closeErrorPopup(): void {
    this.statusErrorPopupElement = closePopupData(this.statusErrorPopupElement);
  }

  private setRegistrationNumberAndSerialNumberValuesWhenError(): void {
    this.vehicleNotFoundForImmat.set(this.paperDeliveryForm.numImmat().value());
    this.numImmatError.set(true);
    this.paperDeliveryForm.numImmat()?.markAsTouched();
    this.paperDeliveryForm.numVin()?.value.set('');
  }

  private setSuccessStatusPopup(): void {
    this.statusSuccessPopupElement = createSuccessPopupData(
      this.translateService.instant('application.paper-delivery.success.title'),
      this.translateService.instant('application.paper-delivery.success.message'),
      this.translateService.instant('application.paper-delivery.success.close-label'),
      undefined,
      this.translateService.instant('application.paper-delivery.success.action-title')
    );
    this.statusSuccessPopupElement = { ...this.statusSuccessPopupElement, iconAction: 'arrow-right' };
    this.cdr.markForCheck();
  }

  private setErrorStatusPopup(): void {
    this.statusErrorPopupElement = createErrorPopupData(
      this.translateService.instant('application.paper-delivery.error.title'),
      this.translateService.instant('application.paper-delivery.error.message'),
      this.translateService.instant('application.paper-delivery.error.close-label'),
      AUTO_CLOSE_DELAY_TEN_SECONDS
    );
    this.cdr.markForCheck();
  }
}
